<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER & BREADCRUMBS ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center gap-2 mb-2">
          <span class="rm-badge-section">تفاصيل الرحلة</span>
          <div class="rm-breadcrumb">
            <router-link to="/trips" class="rm-bc-link">إدارة الرحلات</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <router-link v-if="routeInfo.id" :to="`/trips/route/${routeInfo.id}`" class="rm-bc-link">{{ routeInfo.name }}</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <span class="rm-bc-item rm-bc-active">{{ tripInfo.trip_reference || 'تفاصيل الرحلة' }}</span>
          </div>
        </div>
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="rm-trip-badge rm-badge-lg">{{ tripInfo.trip_reference || ('T-' + String(tripId).padStart(4, '0')) }}</span>
          <span class="rm-code-badge rm-badge-lg">{{ routeInfo.route_code || ('R-' + String(routeInfo.id).padStart(4, '0')) }}</span>
          <h1 class="rm-main-title mb-0">{{ routeInfo.name }}</h1>
        </div>
        <p class="rm-subtitle mt-1">عرض المسار التفصيلي للرحلة ومواعيد الوصول المقدرة لكل محطة على الخريطة</p>
      </div>

      <div class="rm-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="goBack"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          العودة للمسار
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="exportTimetableCsv"
          :disabled="stops.length === 0"
        >
          <v-icon right size="18" class="ms-1">mdi-download</v-icon>
          تصدير الجدول CSV
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-primary"
          @click="goToEditTrip"
        >
          <v-icon right size="18" class="ms-1">mdi-pencil-outline</v-icon>
          تعديل الرحلة
        </v-btn>
      </div>
    </div>

    <!-- ==================== 6 SUMMARY CARDS ==================== -->
    <div class="rm-cards-grid rm-grid-6 mb-6">
      <!-- Card 1: كود الرحلة -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">كود الرحلة</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text font-monospace">{{ tripInfo.trip_reference || '—' }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">المرجع التشغيلي المشتق من المعرف</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="22" color="#082C69">mdi-tag-outline</v-icon>
        </div>
      </div>

      <!-- Card 2: المسار -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسار المعتمد</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text text-navy text-truncate" style="max-width: 140px;">{{ routeInfo.name || '—' }}</span>
            <v-skeleton-loader v-else type="text" width="80" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">{{ routeInfo.stops_count || 0 }} محطات توقف</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="22" color="#10B981">mdi-routes</v-icon>
        </div>
      </div>

      <!-- Card 3: وقت الانطلاق -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">وقت الانطلاق</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text text-navy">{{ formatTime(tripInfo.first_stop_time) }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">من المحطة الأولى</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="22" color="#F59E0B">mdi-clock-start</v-icon>
        </div>
      </div>

      <!-- Card 4: وقت الوصول المتوقع -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">وقت الوصول المتوقع</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text text-navy">{{ formatTime(tripInfo.last_stop_time) }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">إلى آخر محطة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-purple">
          <v-icon size="22" color="#8B5CF6">mdi-clock-end</v-icon>
        </div>
      </div>

      <!-- Card 5: المسافة الكلية -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسافة الكلية</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text">{{ routeInfo.canonical_distance ? `${routeInfo.canonical_distance} كم` : '—' }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">مسار Google الرسمي</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="22" color="#082C69">mdi-map-marker-distance</v-icon>
        </div>
      </div>

      <!-- Card 6: أيام التشغيل -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">أيام التشغيل</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text">{{ tripInfo.operating_days || 'يوميًا' }}</span>
            <v-skeleton-loader v-else type="text" width="70" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">الجدول الأسبوعي</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="22" color="#10B981">mdi-calendar-check-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== ROW: MAP (60%) + STOPS TIMELINE (40%) ==================== -->
    <div class="rm-paired-row mb-6">
      <!-- 1. Google Map Container -->
      <div class="rm-col-map">
        <div class="rm-card rm-map-card rm-full-height">
          <div class="rm-card-header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-map-search-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">خريطة مسار الرحلة</h2>
                <p class="rm-card-subtitle">المسار الجغرافي ونقاط المحطات المعتمدة للرحلة</p>
              </div>
            </div>

            <v-btn
              depressed
              small
              class="rm-btn-secondary"
              @click="fitMapBounds"
            >
              <v-icon size="16" class="me-1">mdi-fit-to-screen-outline</v-icon>
              إظهار المسار بالكامل
            </v-btn>
          </div>

          <!-- Map Component -->
          <div class="rm-map-view-wrapper">
            <GoogleMapLoader
              ref="mapLoader"
              :enabled="false"
              :center="center"
              :selected="selectedItem"
              :zoom="zoom"
              :apiKey="apiKey"
              :markers="markers"
              :polylines="polyline"
            />
          </div>

          <!-- Map Legend -->
          <div class="rm-map-footer-legend d-flex align-center flex-wrap gap-4 mt-3 pt-2 border-top">
            <span class="rm-legend-item">
              <span class="rm-legend-dot rm-dot-start"></span>
              نقطة البداية ({{ stops.length > 0 ? stops[0].name : 'المحطة الأولى' }})
            </span>
            <span class="rm-legend-item">
              <span class="rm-legend-dot rm-dot-end"></span>
              نقطة النهاية ({{ stops.length > 1 ? stops[stops.length - 1].name : 'المحطة الأخيرة' }})
            </span>
            <span class="rm-legend-item">
              <span class="rm-legend-dot rm-dot-intermediate"></span>
              محطات التوقف الوسيطة
            </span>
          </div>
        </div>
      </div>

      <!-- 2. Stops Schedule Timeline -->
      <div class="rm-col-stops">
        <div class="rm-card rm-stops-card rm-full-height">
          <div class="rm-card-header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-format-list-numbered-rtl</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">جدول مواعيد المحطات</h2>
                <p class="rm-card-subtitle">توقيت الوصول المقدر (ETA) لكل محطة</p>
              </div>
            </div>
            <span class="rm-table-count">{{ stops.length }} محطة</span>
          </div>

          <!-- Stops List -->
          <div class="rm-stops-scroll-wrapper">
            <div
              v-for="(stop, index) in stops"
              :key="stop.stop_id || index"
              class="rm-stop-timeline-item"
              :class="{ 'item-selected': selectedStopIdx === index }"
              @click="selectStop(stop, index)"
            >
              <!-- Order Indicator -->
              <div class="rm-stop-order-col">
                <span
                  class="rm-order-circle"
                  :class="{
                    'circle-first': index === 0,
                    'circle-last': index === stops.length - 1 && stops.length > 1,
                    'circle-mid': index > 0 && index < stops.length - 1,
                  }"
                >
                  {{ index + 1 }}
                </span>
                <div v-if="index < stops.length - 1" class="rm-timeline-line"></div>
              </div>

              <!-- Stop Details -->
              <div class="rm-stop-info-col flex-grow-1">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="font-weight-bold text-navy">{{ stop.name }}</span>
                  <!-- ETA -->
                  <span class="rm-eta-pill">
                    <v-icon x-small color="#082C69" class="me-1">mdi-clock-outline</v-icon>
                    {{ stop.eta ? formatTime(stop.eta) : '—' }}
                  </span>
                </div>

                <div class="d-flex align-center justify-space-between text-caption text--secondary">
                  <span class="text-truncate" style="max-width: 200px;">{{ stop.address || '—' }}</span>
                  <span v-if="stop.road_minutes_from_previous > 0" class="rm-inter-time-tag">
                    + {{ stop.road_minutes_from_previous }} دقيقة
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";
import { Keys } from "/src/config.js";
import { exportToCsv } from "@/utils/csvExport";

export default {
  name: "RouteMakingTripDetails",
  components: {
    VueElementLoading,
    GoogleMapLoader,
  },
  data() {
    return {
      isLoading: false,
      tripId: null,
      apiKey: Keys.GOOGLE_MAPS_API_KEY,
      tripInfo: {},
      routeInfo: {},
      stops: [],
      polyline: [],
      markers: [],
      center: { lat: 30.0444, lng: 31.2357 },
      zoom: 12,
      selectedItem: null,
      selectedStopIdx: null,
    };
  },
  created() {
    this.tripId = this.$route.params.trip_id;
    if (this.tripId) {
      this.fetchTripDetails();
    } else {
      this.notifyUser("error", "خطأ", "معرف الرحلة غير محدد");
      this.$router.push("/trips");
    }
  },
  methods: {
    notifyUser(type, title, text) {
      if (this.$notify) {
        this.$notify({
          title: title || (type === "success" ? "تم بنجاح" : type === "warn" ? "تنبيه" : "خطأ"),
          text: text,
          type: type === "warn" ? "warn" : type,
        });
      } else if (this.$swal) {
        this.$swal(title || (type === "success" ? "تم بنجاح" : "خطأ"), text, type === "warn" ? "warning" : type);
      } else {
        alert(text);
      }
    },
    fetchTripDetails() {
      this.isLoading = true;
      axios
        .get(`/trips/parent-trip/${this.tripId}`)
        .then((response) => {
          if (response.data) {
            this.tripInfo = response.data.trip || {};
            this.routeInfo = response.data.route || {};
            this.stops = response.data.stops || [];

            if (response.data.map) {
              const poly = response.data.map.polyline || [];
              this.polyline = poly.length > 0 ? [{ data: poly, strokeColor: '#082C69', strokeWeight: 5, strokeOpacity: 0.9 }] : [];
              this.markers = response.data.map.markers || [];
            }

            // Set map center to first stop or center of polyline
            if (this.markers.length > 0 && this.markers[0].position) {
              this.center = this.markers[0].position;
            } else if (this.polyline.length > 0 && this.polyline[0].data && this.polyline[0].data.length > 0) {
              this.center = this.polyline[0].data[0];
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching trip details:", error);
          this.notifyUser("error", "خطأ", "تعذر تحميل تفاصيل الرحلة");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    exportTimetableCsv() {
      exportToCsv({
        filename: `جدول-مواعيد-الرحلة-${this.tripInfo.trip_reference || this.tripId}-${new Date().toISOString().slice(0, 10)}.csv`,
        metadata: [
          { label: "كود الرحلة", value: this.tripInfo.trip_reference || `T-${this.tripId}` },
          { label: "المسار", value: this.routeInfo.name || "" },
          { label: "كود المسار", value: this.routeInfo.route_code || "" },
          { label: "وقت البداية", value: this.formatTime(this.tripInfo.first_stop_time) },
          { label: "وقت النهاية", value: this.formatTime(this.tripInfo.last_stop_time) },
          { label: "المدة المتوقعة", value: this.tripInfo.expected_duration || this.routeInfo.canonical_duration || "" },
          { label: "أيام التشغيل", value: this.tripInfo.operating_days || "" },
        ],
        headers: [
          "ترتيب المحطة",
          "اسم المحطة",
          "نوع المحطة",
          "وقت الوصول المتوقع (ETA)",
          "زمن الطريق من المحطة السابقة (دقيقة)",
          "العنوان",
        ],
        rows: this.stops.map((s, idx) => [
          s.order || idx + 1,
          s.name || "",
          idx === 0 ? "محطة البداية" : (idx === this.stops.length - 1 ? "محطة النهاية" : "محطة توقف وسطية"),
          this.formatTime(s.eta),
          s.road_minutes_from_previous || s.road_minutes || 0,
          s.address || "",
        ]),
      });
    },
    formatTime(timeStr) {
      if (!timeStr) return "—";
      const parts = timeStr.split(":");
      if (parts.length < 2) return timeStr;
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "م" : "ص";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    },
    selectStop(stop, index) {
      this.selectedStopIdx = index;
      if (stop.lat && stop.lng) {
        this.center = { lat: stop.lat, lng: stop.lng };
        this.zoom = 15;
      }
    },
    fitMapBounds() {
      if (this.markers.length > 0) {
        this.center = this.markers[0].position;
        this.zoom = 12;
      }
    },
    goBack() {
      if (this.routeInfo.id) {
        this.$router.push(`/trips/route/${this.routeInfo.id}`);
      } else {
        this.$router.push("/trips");
      }
    },
    goToEditTrip() {
      this.$router.push(`/trips/edit/trip=${this.tripId}&action=edit`);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  color: #1E293B;
  padding: 8px 4px 40px 4px;
}

/* Header */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  display: inline-flex;
  align-items: center;
  background: #E8EEF5;
  color: #082C69;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 6px;
}

.rm-breadcrumb {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  color: #64748B;
}

.rm-bc-link {
  color: #64748B;
  text-decoration: none;
  transition: color 0.2s;
}

.rm-bc-link:hover {
  color: #082C69;
}

.rm-bc-active {
  color: #082C69;
  font-weight: 600;
}

.rm-main-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.3;
}

.rm-subtitle {
  font-size: 0.875rem;
  color: #64748B;
  margin: 0;
}

.rm-badge-lg {
  font-size: 0.95rem !important;
  padding: 4px 10px !important;
}

/* Buttons */
.rm-btn-primary {
  background: #082C69 !important;
  color: #FFFFFF !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 44px !important;
  border-radius: 10px !important;
  padding: 0 20px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px rgba(8, 44, 105, 0.25) !important;
  transition: all 0.2s ease !important;
}

.rm-btn-primary:hover {
  background: #061B40 !important;
  box-shadow: 0 6px 18px rgba(8, 44, 105, 0.35) !important;
  transform: translateY(-1px);
}

.rm-btn-secondary {
  background: #FFFFFF !important;
  color: #475569 !important;
  border: 1px solid #CBD5E1 !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  border-radius: 10px !important;
  padding: 0 16px !important;
  height: 44px !important;
  text-transform: none !important;
  transition: all 0.2s ease !important;
}

.rm-btn-secondary:hover {
  background: #F8FAFC !important;
  border-color: #94A3B8 !important;
  color: #082C69 !important;
}

/* Cards Grid */
.rm-cards-grid {
  display: grid;
  gap: 14px;
}

.rm-grid-6 {
  grid-template-columns: repeat(6, 1fr);
}

@media (max-width: 1400px) {
  .rm-grid-6 {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .rm-grid-6 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .rm-grid-6 {
    grid-template-columns: 1fr;
  }
}

.rm-stat-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rm-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.rm-stat-label {
  display: block;
  font-size: 0.775rem;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 3px;
}

.rm-stat-value-text {
  font-size: 1.05rem;
  font-weight: 800;
  color: #082C69;
  line-height: 1.3;
}

.font-monospace {
  font-family: monospace;
}

.rm-stat-caption {
  display: block;
  font-size: 0.7rem;
  color: #94A3B8;
  margin-top: 3px;
}

.rm-stat-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-icon-blue { background: #EFF6FF; }
.rm-icon-green { background: #ECFDF5; }
.rm-icon-amber { background: #FFFBEB; }
.rm-icon-purple { background: #F5F3FF; }

/* Paired Row (Map 60% + Stops 40%) */
.rm-paired-row {
  display: grid;
  grid-template-columns: 60% 40%;
  gap: 20px;
}

@media (max-width: 1024px) {
  .rm-paired-row {
    grid-template-columns: 1fr;
  }
}

.rm-card {
  background: #FFFFFF;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-full-height {
  height: 100%;
}

.rm-icon-header-box {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #EFF6FF;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-card-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 0.775rem;
  color: #64748B;
  margin: 0;
}

.rm-table-count {
  font-size: 0.8125rem;
  color: #64748B;
  font-weight: 600;
  background: #F8FAFC;
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
}

/* Map Wrapper */
.rm-map-view-wrapper {
  height: 480px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
  position: relative;
}

.rm-map-footer-legend {
  font-size: 0.775rem;
  color: #64748B;
}

.rm-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.rm-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.rm-dot-start { background: #10B981; }
.rm-dot-end { background: #EF4444; }
.rm-dot-intermediate { background: #082C69; }

/* Stops Timeline List */
.rm-stops-scroll-wrapper {
  max-height: 520px;
  overflow-y: auto;
  padding-left: 6px;
}

.rm-stop-timeline-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.rm-stop-timeline-item:hover {
  background: #F8FAFC;
}

.rm-stop-timeline-item.item-selected {
  background: #EFF6FF;
}

.rm-stop-order-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 12px;
  position: relative;
}

.rm-order-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #FFFFFF;
  z-index: 1;
}

.circle-first { background: #10B981; }
.circle-last { background: #EF4444; }
.circle-mid { background: #082C69; }

.rm-timeline-line {
  width: 2px;
  height: 38px;
  background: #E2E8F0;
  margin-top: 4px;
}

.rm-eta-pill {
  display: inline-flex;
  align-items: center;
  background: #EFF6FF;
  border: 1px solid #DBEAFE;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.775rem;
  font-weight: 700;
  color: #082C69;
}

.rm-inter-time-tag {
  background: #FEF3C7;
  color: #92400E;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

/* Badges */
.rm-code-badge {
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.825rem;
  background: #EFF6FF;
  color: #082C69;
  border: 1px solid #DBEAFE;
  padding: 3px 8px;
  border-radius: 6px;
}

.rm-trip-badge {
  display: inline-block;
  font-family: monospace;
  font-weight: 700;
  font-size: 0.825rem;
  background: #F1F5F9;
  color: #334155;
  border: 1px solid #CBD5E1;
  padding: 3px 8px;
  border-radius: 6px;
}

.text-navy {
  color: #082C69;
}

.border-top {
  border-top: 1px solid #E2E8F0;
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-4 { gap: 16px; }
</style>
